import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Square, Search, Loader, AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import { fetchDdlcServices, fetchDdlcStreams, fetchDdlcLatestLogs, fetchDdlcSearchLogs } from './ddlcApi';

const LEVELS = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];
const POLL_INTERVAL_MS = 3000;

const LEVEL_BADGE = {
  TRACE:    'bg-gray-100 text-gray-500 border-gray-200',
  DEBUG:    'bg-blue-100 text-primaryDark border-blue-200',
  INFO:     'bg-green-100 text-green-700 border-green-200',
  WARN:     'bg-yellow-100 text-yellow-700 border-yellow-200',
  ERROR:    'bg-red-100 text-red-600 border-red-200',
  CRITICAL: 'bg-red-200 text-red-800 border-red-300 font-bold',
};

const LEVEL_ROW = {
  TRACE:    '',
  DEBUG:    '',
  INFO:     '',
  WARN:     'bg-yellow-50/40',
  ERROR:    'bg-red-50/40',
  CRITICAL: 'bg-red-100/60',
};

const LevelBadge = ({ level }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs border font-mono ${LEVEL_BADGE[level] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
    {level}
  </span>
);

const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};


const LogTable = ({ entries, loading }) => {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries.length]);

  if (loading && entries.length === 0) {
    return (
      <div className="flex justify-center items-center py-16 text-gray-400">
        <Loader size={20} className="animate-spin mr-2" /> Loading logs...
      </div>
    );
  }

  if (!loading && entries.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-gray-400">
        <FileText size={32} className="mb-2 opacity-40" />
        <span>No log entries found</span>
      </div>
    );
  }

  return (
    <div className="overflow-auto flex-1 rounded-lg border border-gray-100">
      <table className="min-w-full text-xs font-mono">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left text-gray-500 font-semibold whitespace-nowrap w-44">Timestamp</th>
            <th className="px-3 py-2 text-left text-gray-500 font-semibold w-24">Level</th>
            <th className="px-3 py-2 text-left text-gray-500 font-semibold w-40">File</th>
            <th className="px-3 py-2 text-left text-gray-500 font-semibold">Message</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[...entries].sort((a, b) => (a.ts_ns ?? 0) - (b.ts_ns ?? 0)).map((entry, i) => (
            <tr key={i} className={`hover:bg-blue-50/30 transition-colors ${LEVEL_ROW[entry.level] ?? ''}`}>
              <td className="px-3 py-1.5 text-gray-400 whitespace-nowrap">{formatTs(entry.timestamp)}</td>
              <td className="px-3 py-1.5"><LevelBadge level={entry.level} /></td>
              <td className="px-3 py-1.5 text-gray-500 truncate max-w-[160px]" title={entry.name}>{entry.name}</td>
              <td className="px-3 py-1.5 text-gray-800 whitespace-pre-wrap break-all">{entry.line}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div ref={bottomRef} />
    </div>
  );
};

const DdlcPipelineLogs = () => {
  const [activeTab, setActiveTab]         = useState('live');
  const [services, setServices]           = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [streams, setStreams]             = useState([]);
  const [selectedStream, setSelectedStream] = useState(null); // { type, name } | null
  const [selectedLevel, setSelectedLevel] = useState('');
  const [entries, setEntries]             = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [isPolling, setIsPolling]         = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const newestTsRef  = useRef(null);  // ts_ns of most-recent entry seen
  const pollTimerRef = useRef(null);

  // Load services on mount
  useEffect(() => {
    fetchDdlcServices()
      .then(list => {
        setServices(list);
        if (list.length > 0) setSelectedService(list[0]);
      })
      .catch(err => setError(`Failed to load services: ${err.message}`));
  }, []);

  // Load streams whenever service changes
  useEffect(() => {
    if (!selectedService) return;
    setStreams([]);
    setSelectedStream(null);
    fetchDdlcStreams(selectedService)
      .then(list => setStreams(list))
      .catch(err => setError(`Failed to load streams: ${err.message}`));
  }, [selectedService]);

  // Stop polling when unmounted or tab changes
  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (isPolling) stopPolling();
    setEntries([]);
    newestTsRef.current = null;
  }, [selectedService, selectedStream, selectedLevel, activeTab]);

  const stopPolling = () => {
    clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
    setIsPolling(false);
  };

  const fetchLatest = useCallback(async (isAppend = false) => {
    if (!selectedService) return;
    if (!isAppend) setLoading(true);
    try {
      const allEntries = await fetchDdlcLatestLogs({
        service: selectedService,
        level: selectedLevel || undefined,
        type: selectedStream?.type,
        name: selectedStream?.name,
      });

      if (!isAppend) {
        // Initial load — show everything and record the newest ts_ns
        setEntries(allEntries);
        if (allEntries.length > 0) {
          const maxTsNs = Math.max(...allEntries.map(e => e.ts_ns ?? 0));
          newestTsRef.current = maxTsNs;
        }
      } else {
        // Poll — keep only entries strictly newer than what we've already shown
        const prev = newestTsRef.current;
        const fresh = prev != null
          ? allEntries.filter(e => (e.ts_ns ?? 0) > prev)
          : allEntries;
        if (fresh.length > 0) {
          const maxTsNs = Math.max(...fresh.map(e => e.ts_ns ?? 0));
          newestTsRef.current = maxTsNs;
          setEntries(existing => [...existing, ...fresh]);
        }
      }
      setError(null);
    } catch (err) {
      setError(`Failed to fetch logs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedService, selectedStream, selectedLevel]);

  const handleStartPolling = () => {
    setEntries([]);
    newestTsRef.current = null;
    fetchLatest(false);
    setIsPolling(true);
    pollTimerRef.current = setInterval(() => fetchLatest(true), POLL_INTERVAL_MS);
  };

  const handleStopPolling = () => stopPolling();

  const handleSearch = async () => {
    if (!selectedService || !searchQuery.trim()) return;
    setSearchLoading(true);
    setEntries([]);
    try {
      const results = await fetchDdlcSearchLogs({
        service: selectedService,
        search: searchQuery.trim(),
        level: selectedLevel || undefined,
        type: selectedStream?.type,
        name: selectedStream?.name,
      });
      setEntries(results);
      setError(null);
    } catch (err) {
      setError(`Search failed: ${err.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Controls card */}
      <div className="bg-white/80 backdrop-blur-md rounded-lg shadow border border-gray-100/50 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Service */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-700">Service</label>
            <select
              value={selectedService}
              onChange={e => setSelectedService(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-[160px]"
            >
              {services.length === 0 && <option value="">Loading...</option>}
              {services.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Stream */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-700">Stream</label>
            <select
              value={selectedStream ? JSON.stringify(selectedStream) : ''}
              onChange={e => setSelectedStream(e.target.value ? JSON.parse(e.target.value) : null)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-[180px]"
            >
              <option value="">All streams</option>
              {streams.map(s => (
                <option key={`${s.type}:${s.name}`} value={JSON.stringify(s)}>
                  {s.name} ({s.type})
                </option>
              ))}
            </select>
          </div>

          {/* Level */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-700">Min Level</label>
            <select
              value={selectedLevel}
              onChange={e => setSelectedLevel(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="">All levels</option>
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          {/* Tab toggles */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden ml-auto">
            <button
              onClick={() => { setActiveTab('live'); setEntries([]); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'live' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Live Logs
            </button>
            <button
              onClick={() => { setActiveTab('search'); if (isPolling) stopPolling(); setEntries([]); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'search' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Search
            </button>
          </div>
        </div>

        {/* Live polling controls */}
        {activeTab === 'live' && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            {!isPolling ? (
              <button
                onClick={handleStartPolling}
                disabled={!selectedService}
                className="flex items-center text-success text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-successLight disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play size={16} className="mr-1" /> Start Polling
              </button>
            ) : (
              <button
                onClick={handleStopPolling}
                className="flex items-center text-red-500 text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-red-50"
              >
                <Square size={16} className="mr-1" /> Stop Polling
              </button>
            )}
            <button
              onClick={() => fetchLatest(false)}
              disabled={!selectedService || loading}
              className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader size={16} className="mr-1 animate-spin" /> : <RefreshCw size={16} className="mr-1" />}
              Refresh
            </button>
            {isPolling && (
              <span className="flex items-center gap-1.5 text-xs text-success font-medium">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                Polling every {POLL_INTERVAL_MS / 1000}s
              </span>
            )}
            {entries.length > 0 && (
              <span className="text-xs text-gray-400 ml-auto">{entries.length} entries</span>
            )}
          </div>
        )}

        {/* Search controls */}
        {activeTab === 'search' && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            <input
              type="text"
              placeholder="Search in logs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <button
              onClick={handleSearch}
              disabled={!selectedService || !searchQuery.trim() || searchLoading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {searchLoading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
              Search
            </button>
            {entries.length > 0 && (
              <span className="text-xs text-gray-400">{entries.length} results</span>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50/90 border-l-4 border-red-500 text-red-700 p-3 flex items-center rounded-lg text-sm">
          <AlertTriangle size={16} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-700 hover:text-red-900">&times;</button>
        </div>
      )}

      {/* Log table */}
      <div className="flex-1 bg-white/80 backdrop-blur-md rounded-lg shadow border border-gray-100/50 flex flex-col overflow-hidden min-h-0">
        <LogTable entries={entries} loading={loading || searchLoading} />
      </div>
    </div>
  );
};

export default DdlcPipelineLogs;
